import { useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { FeeGuideModal } from './FeeGuideModal';
import { useFeeGuideAimCode } from '../lib/feeGuide';
import { isPriceGuaranteeEnabled } from '../lib/priceGuarantee';

interface HeroSolidBGProps {
  tokenPrefix?: string;
  title?: string;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
  secondaryCtaLabel?: string;
  secondaryCtaUrl?: string;
  // When set, the secondary CTA's URL defaults to this property field (e.g.
  // `property_apply_now_url`) instead of the static `secondaryCtaUrl`. Used by
  // the Apply Now hero so the button routes to the property's application URL.
  secondaryCtaUrlField?: string;
  // When true, the secondary CTA opens the Property Fee Guide popup instead of
  // navigating to `secondaryCtaUrl`. Used by the Floor Plans hero.
  secondaryCtaFeeGuide?: boolean;
  // Whether to render the overlaid navigation CTA button on the hero. Interior
  // pages hide it by default for a clean hero with no button; opt in per page
  // by setting this to true (e.g. the Apply Now hero). Ignored when
  // `secondaryCtaFeeGuide` is set — the fee-guide button always renders (below
  // the image).
  showSecondaryCta?: boolean;
  // Swaps the hero button's fill and text colors: white fill with black text,
  // inverting to a black fill on hover. Used by the Apply Now hero so its CTA
  // reads as the primary action against the dark hero image.
  secondaryCtaInverted?: boolean;
  // Widens the hero button's horizontal padding for pages where the CTA is the
  // page's main action (e.g. Apply Now) and should carry more visual weight.
  secondaryCtaWide?: boolean;
  // Optional overline heading rendered above the blurb inside the hero box.
  // Supplied per page via pages.json as richtext HTML (may embed a
  // field-token span, e.g. property_bedrooms_range). When omitted, no
  // overline renders — keeps the shared hero blank on pages that don't set it.
  overlineDefault?: string;
  // When true, use a tight bottom pad so the next section can sit closer
  // (e.g. Virtual Tours list directly under the hero).
  compactBottom?: boolean;
}

export function HeroSolidBG({
  tokenPrefix,
  title = 'Page',
  blurbField,
  defaultText,
  ctaLabel = 'Back To Home',
  ctaUrl = '/',
  // Transparent "button" CTA centered in the hero box. Defaults route visitors
  // to the Floor Plans page; the Floor Plans page itself overrides these props
  // (via pages.json) to point at Amenities instead.
  secondaryCtaLabel = 'Find Your Home',
  secondaryCtaUrl = '/floor-plans',
  secondaryCtaUrlField,
  secondaryCtaFeeGuide = false,
  showSecondaryCta = false,
  secondaryCtaInverted = false,
  secondaryCtaWide = false,
  overlineDefault,
  compactBottom = false,
}: HeroSolidBGProps) {
  const prefix = tokenPrefix || 'herosolidbg';
  const titlePath = `${prefix}.title`;
  const overlinePath = `${prefix}.overline`;
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;
  const secondaryCtaPath = `${prefix}.secondary_cta`;
  const bgImagePath = `${prefix}.bg_image`;

  // Rendered as a "← Back To Home" text link under the title on mobile
  // (centered) and top-right from sm up — not a button over the image.
  // Defaults to home but stays editable per page; property_apply_now_url /
  // property_website_url remain available to editors via suggestedFields.
  const ctaLinkDef: SlotGroupDefinition = {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  // Single static hero image (not a gallery carousel). Defaults to the
  // property featured image — image only, never featured video.
  const bgImageDef: SlotGroupDefinition = {
    label: 'Hero Image',
    fields: {
      image: {
        label: 'Image',
        type: 'image',
        defaultField: 'property_featured_image',
      },
      alt: {
        label: 'Alt Text',
        type: 'text',
        defaultValue: 'Page hero image',
      },
    },
  };

  // Transparent call-to-action button overlaid in the hero box. Editable per
  // page; the URL/label defaults come from props so each page can point it at
  // the most relevant next step.
  const secondaryCtaDef: SlotGroupDefinition = {
    label: 'Hero Button',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        // Bind to a property field when a page supplies `secondaryCtaUrlField`
        // (e.g. the Apply Now hero → `property_apply_now_url`); otherwise fall
        // back to the static per-page `secondaryCtaUrl`.
        ...(secondaryCtaUrlField
          ? { defaultField: secondaryCtaUrlField }
          : { defaultValue: secondaryCtaUrl }),
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Button Text',
        type: 'text',
        defaultValue: secondaryCtaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };

  const cta = useSlotGroup(ctaPath, ctaLinkDef);
  const secondaryCta = useSlotGroup(secondaryCtaPath, secondaryCtaDef);
  const bgImage = useSlotGroup(bgImagePath, bgImageDef);
  // When True Price Guarantee is on, that section owns the Fee Guide CTA and
  // sits flush under the hero image — so hide the hero's below-image button.
  const priceGuaranteeEnabled = isPriceGuaranteeEnabled(
    usePropertyField('custom_show_price_guarantee'),
  );
  const hasFeeGuideAimCode = Boolean(useFeeGuideAimCode());
  // Hide the fee-guide button when the property has no AIM code — no fallback
  // community. TPG owns the same CTA when that section is on.
  const showFeeGuideBelowHero =
    secondaryCtaFeeGuide && !priceGuaranteeEnabled && hasFeeGuideAimCode;
  // Flush the next section under the image when TPG replaces the hero fee guide.
  const flushBottomForPriceGuarantee = secondaryCtaFeeGuide && priceGuaranteeEnabled;

  // Fee-guide popup state — only used when secondaryCtaFeeGuide is enabled.
  const [feeGuideOpen, setFeeGuideOpen] = useState(false);

  // Shared classes for the transparent hero button, whether rendered as a link
  // (navigation) or a button (fee-guide popup trigger). Vertical padding is left
  // to each call site so it can set its own height without two competing
  // Tailwind `py-*` utilities in one class string.
  const secondaryCtaClasses = `hero-solid-cta inline-flex items-center justify-center whitespace-nowrap text-xs sm:text-sm md:text-base capitalize border transition-colors duration-300 ${
    secondaryCtaWide ? 'px-12 sm:px-20' : 'px-6'
  } ${
    secondaryCtaInverted
      ? 'border-white bg-white text-black hover:border-black hover:bg-black hover:text-white'
      : 'border-black bg-black text-white hover:bg-white hover:text-black'
  }`;

  return (
    <section
      className={`bg-[var(--vibe-color-background)] px-3 sm:px-5 md:px-6 ${
        flushBottomForPriceGuarantee
          ? 'pb-0'
          : compactBottom
            ? 'pb-5 md:pb-6'
            : 'pb-10 md:pb-14'
      }`}
      // The fixed Header already publishes a layout spacer equal to its full
      // height (offer banner + nav bar), so the hero must NOT re-add
      // --offer-banner-height here or the banner height is counted twice. A
      // constant on top of that spacer gives the title breathing room below
      // the nav.
      style={{ paddingTop: '3.25rem' }}
    >
      <div className="w-full">

        {/* Title row — on mobile: centered stack (title above "← Back To Home").
            From sm up: title left, back link right. */}
        <div className="flex flex-col items-center gap-5 sm:flex-row sm:items-center sm:justify-between sm:gap-4 mb-10 sm:mb-8">
          <RichTextContent
            path={titlePath}
            defaultValue={`<p>${title}</p>`}
            as="h1"
            className="hero-solid-title font-heading w-full text-3xl sm:text-2xl md:text-3xl font-bold tracking-wider leading-tight break-words text-center sm:w-auto sm:text-left text-[var(--vibe-color-text)] m-0"
          />
          <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
            <a
              href={cta.url || '/'}
              target={cta._target || '_self'}
              className="hero-back-link group/cta inline-flex items-center justify-center gap-2 whitespace-nowrap font-heading text-sm md:text-base font-normal tracking-wider text-[var(--vibe-color-text)] transition-opacity hover:opacity-70"
            >
              <span aria-hidden className="transition-transform duration-300 group-hover/cta:-translate-x-1">←</span>
              <span>{cta.label || ctaLabel}</span>
            </a>
          </EditableSlotGroup>
        </div>

        {/* Boxed single background with the blurb centered on top */}
        <div className="hero-solid-box relative w-full overflow-hidden">
          <EditableSlotGroup
            groupPath={bgImagePath}
            definition={bgImageDef}
            className="absolute inset-0"
          >
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                sizes="100vw"
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-primary" />
            )}
          </EditableSlotGroup>

          {/* Gradient overlay for text contrast — decorative, non-interactive. */}
          <div className="absolute inset-0 z-[3] bg-gradient-to-t from-black/80 via-black/65 to-black/55 pointer-events-none" />

          {/* Content overlay: overline + blurb + transparent CTA button, centered. */}
          <div className="relative z-10 flex flex-col items-center justify-center gap-5 h-full text-center text-white px-6 sm:px-10">
            {/* Optional overline heading above the blurb (e.g. the property's
                bedroom range + "Apartments"). Only renders when a page supplies
                `overlineDefault`, so the shared hero stays blank elsewhere. */}
            {overlineDefault ? (
              <RichTextContent
                path={overlinePath}
                defaultValue={overlineDefault}
                as="h2"
                className="font-body text-lg sm:text-xl md:text-2xl font-normal tracking-wider drop-shadow-md m-0"
              />
            ) : null}
            <RichTextContent
              path={blurbPath}
              defaultField={blurbField}
              defaultValue={defaultText || ''}
              as="div"
              className="page-blurb font-body text-[0.875rem] tracking-normal max-w-3xl mx-auto drop-shadow-md [&_a]:underline"
            />
            {/* In fee-guide mode the CTA is rendered *below* the hero box (see
                after this block) so it sits beneath the image rather than
                overlaid on it. The overlaid navigation CTA renders by default
                on interior pages; a page can hide it via `showSecondaryCta:
                false`. */}
            {secondaryCtaFeeGuide || !showSecondaryCta ? null : (
              <EditableSlotGroup groupPath={secondaryCtaPath} definition={secondaryCtaDef}>
                <a
                  href={secondaryCta.url || secondaryCtaUrl}
                  target={secondaryCta._target || '_self'}
                  className={`${secondaryCtaClasses} py-3`}
                >
                  <span>{secondaryCta.label || secondaryCtaLabel}</span>
                </a>
              </EditableSlotGroup>
            )}
          </div>
        </div>

        {/* Fee-guide CTA below the hero image (Floor Plans). Skipped when True
            Price Guarantee is enabled — that section renders the same CTA. */}
        {showFeeGuideBelowHero ? (
          <div className="mt-6 flex justify-center">
            {/* Wide-but-inset and taller on phones, auto-width from sm up. The
                wrapper div EditableSlotGroup renders is the flex item, so it
                needs the width classes too or the button can't stretch. */}
            <EditableSlotGroup
              groupPath={secondaryCtaPath}
              definition={secondaryCtaDef}
              className="w-[88%] sm:w-auto"
            >
              <button
                type="button"
                onClick={() => setFeeGuideOpen(true)}
                className={`${secondaryCtaClasses} w-full py-4 sm:w-auto sm:py-3`}
              >
                <span>{secondaryCta.label || secondaryCtaLabel}</span>
              </button>
            </EditableSlotGroup>
          </div>
        ) : null}

      </div>

      {showFeeGuideBelowHero ? (
        <FeeGuideModal
          open={feeGuideOpen}
          onClose={() => setFeeGuideOpen(false)}
          title={secondaryCta.label || secondaryCtaLabel}
        />
      ) : null}

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="herosolidbg.title"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.overline"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <RichTextContent
        path="herosolidbg.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="herosolidbg.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.secondary_cta"
        definition={secondaryCtaDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="herosolidbg.bg_image"
        definition={bgImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        /* Boxed hero frame — shorter than the homepage Hero box
           (clamp(440px, 78vh, 880px)) so subpages get a lighter banner. */
        .hero-solid-box {
          height: clamp(300px, 48vh, 520px);
          background: #000;
        }
        /* Sans body face for page blurbs. Leading is tighter on phones so the
           larger mobile type still fits the fixed-height (overflow-hidden)
           hero box for long property blurbs. */
        .page-blurb,
        .page-blurb p,
        .page-blurb span {
          font-family: var(--vibe-font-body) !important;
          letter-spacing: normal !important;
          line-height: 1.75 !important;
        }
        @media (min-width: 640px) {
          .page-blurb,
          .page-blurb p,
          .page-blurb span {
            line-height: 2 !important;
          }
        }
        /* Match header / Community Info CTAs: body face, normal weight, tight tracking. */
        .hero-solid-cta {
          font-family: var(--vibe-font-body) !important;
          font-weight: 400;
          letter-spacing: 0.03em;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HeroSolidBG',
  displayName: 'Hero – Solid Background',
  tokens: {
    'herosolidbg.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Page</p>',
    },
    'herosolidbg.overline': {
      label: 'Overline Title',
      type: 'richtext',
      defaultValue: '',
    },
    'herosolidbg.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'herosolidbg.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'Back To Home',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'herosolidbg.secondary_cta': {
      label: 'Hero Button',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Button Text',
          type: 'text',
          defaultValue: 'Find Your Home',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
    'herosolidbg.bg_image': {
      label: 'Hero Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Page hero image',
        },
      },
    },
  },
};

(HeroSolidBG as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HeroSolidBG;
